import { filter, mapWithIndex } from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import { chain, fromNullable, getOrElse, isNone, map, none, Option, some } from 'fp-ts/Option';
import isEmpty from 'lodash/isEmpty';

import { TemplatePreview } from 'modules/ai/hooks/campaigns/types';
import {
  CampaignData,
  CommonTemplateLocaleData,
  NBACampaign,
  NBACampaignData,
  NBAData,
} from 'modules/ai/nba/types';
import { Locale } from 'modules/domains/template';
import { compactFromArray } from 'utils/fp';
import { TemplateAndProjectLocalesResponse } from 'utils/hooks/locales/useQueryTemplateAndProjectLocales';
import { isUndefined } from 'utils/tsHelpers';

// Default data in event of failure
export const defaultNBAData = {
  audienceCount: 0,
  topCampaigns: [],
  suggestions: [],
};

export const defaultTemplateLocaleData = {
  isLocalesFeatureEnabled: false,
  templates: [],
  templateDefaultLocaleToSet: none,
  templatesLocalesToRemove: none,
};

/**
 @abstract Option<'NBAData> -> number[]
 */
export const getCampaignIds = (maybeNbaData: Option<NBAData>) => {
  if (isNone(maybeNbaData)) {
    return [];
  }
  return pipe(
    maybeNbaData,
    chain((nbaData: NBAData) => (nbaData.topCampaigns ? some(nbaData.topCampaigns) : none)),
    map((nbaTopCampaigns) => nbaTopCampaigns.map((nbaCampaign) => Number(nbaCampaign.campaignId))),
    getOrElse<number[]>(() => []),
  );
};

export const getTemplateIds = (campaigns: Array<CampaignData>) => {
  if (isEmpty(campaigns)) {
    return [];
  }
  return campaigns && campaigns.map((campaign) => campaign.templateId);
};

/**
 @abstract (Option<'NBAData>, Array<'CampaignData>) -> Array<Option<'NBACampaignData>>
 */
export const mergeCampaignData = (
  maybeNbaData: Option<NBAData>,
  campaigns: Array<CampaignData>,
): Array<Option<NBACampaignData>> => {
  if (isNone(maybeNbaData) || isEmpty(campaigns)) {
    return [none];
  }
  return pipe(
    maybeNbaData,
    chain((nbaData: NBAData) =>
      Array.isArray(nbaData.topCampaigns) && nbaData.topCampaigns.length > 0
        ? some(nbaData.topCampaigns)
        : none,
    ),
    map<NBACampaign[], Array<Option<NBACampaignData>>>(
      mapWithIndex((index, nbaCampaign) => {
        if (!campaigns[index]) {
          return none;
        }
        return some({
          ...campaigns[index],
          ...nbaCampaign,
        });
      }),
    ),
    getOrElse<Array<Option<NBACampaignData>>>(() => [none]),
  );
};

/**
 @abstract TemplatePreview | undefined -> Option<'TemplatePreview>
 */
export const parseTemplatesData: (templatePreviews: TemplatePreview) => Option<TemplatePreview> =
  fromNullable;

export const parseTemplateLocaleData = (
  isLocalesFeatureEnabled: boolean,
  deprecatedTemplateLocalesData: Locale[],
  templateLocalesData: TemplateAndProjectLocalesResponse,
): Option<CommonTemplateLocaleData> => {
  if (!isLocalesFeatureEnabled) {
    return isEmpty(deprecatedTemplateLocalesData)
      ? none
      : some({
          isLocalesFeatureEnabled,
          templates: deprecatedTemplateLocalesData,
          templateDefaultLocaleToSet: none,
          templatesLocalesToRemove: none,
        });
  }

  if (isEmpty(templateLocalesData)) {
    return none;
  }

  return some({
    isLocalesFeatureEnabled,
    isTemplateDefaultLocaleModifiable: templateLocalesData.isTemplateDefaultLocaleModifiable,
    templates: templateLocalesData.activeTemplateLocales || [],
    projectLocales: templateLocalesData.allProjectLocales || [],
    templateDefaultLocale: templateLocalesData.templateDefaultLocale,
    templateDefaultLocaleToSet: fromNullable(templateLocalesData.templateDefaultLocale?.tag),
    templatesLocalesToRemove: none,
  });
};

export const ENGLISH_LANGUAGE_NAME = 'English';

export const isLocaleEnglish = (locale: Locale | undefined) => {
  return locale?.languageName === ENGLISH_LANGUAGE_NAME;
};

export const getDefaultLocale = (
  templateData: CommonTemplateLocaleData,
): CommonTemplateLocaleData => {
  return {
    ...templateData,
    templateDefaultLocaleToSet: fromNullable(templateData.templates.find(isLocaleEnglish)?.tag),
  };
};

/**
 * When `LocaleImprovements` is ON, we need to manually call remove for all locales except the default locale that already exists
 * removeFromLocales will filter out the locale you want to remove from the templates list
 */
export const removeFromLocales = (
  templateData: CommonTemplateLocaleData,
): CommonTemplateLocaleData => {
  return {
    ...templateData,
    templatesLocalesToRemove: pipe(
      templateData.templateDefaultLocaleToSet,
      map((localeToSet) =>
        filter((template: Locale) => template?.tag !== localeToSet)(templateData.templates),
      ),
      compactFromArray,
    ),
  };
};

export const setTemplateDataToRemove = (
  parsedTemplateData: Option<CommonTemplateLocaleData>,
): Option<CommonTemplateLocaleData> => {
  return pipe(parsedTemplateData, map(getDefaultLocale), map(removeFromLocales));
};

export const createShouldSkipLocaleQuery =
  (templateId: number | undefined) => (localesFlagStatus: boolean) => {
    if (isUndefined(templateId)) {
      return true;
    }
    return localesFlagStatus;
  };
